import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DateValueType } from "react-tailwindcss-datepicker";
import { faBullhorn, faLayerGroup, faUsers } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";

import { CampaignChip } from "../schema/campaignChip";
import { DateFilterPill } from "./DateFilterPill";
import { FilterPill, FilterPillCheckList } from "./FilterPill";
import { ListFilterQuickFilters } from "./ListFilterQuickFilters";
import { useSenderOptions } from "../hooks/useSenderOptions";
import { ANALYTICS_HEADER_HEIGHT_VAR } from "../hooks/useStickyHeaderHeight";
import {
	AnalyticsListContentFilterKey,
	AnalyticsListOptions,
} from "../hooks/useAnalyticsListOptions";
import { selectedOptionsFirst } from "../utils/selectedOptionsFirst";
import { toSelectedList } from "../utils/selectedList";

export { buildShortcuts } from "./DateFilterPill";

export interface AnalyticsFilterBarProps {
	dateFromToValue: DateValueType;
	onDateChange: (value: DateValueType) => void;

	listOptions: AnalyticsListOptions;
	onListContentFilterToggle: (key: AnalyticsListContentFilterKey) => void;
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	onListSelect: (lists: CustomListsAutocompleteForSequencerItem[]) => void;

	campaigns: CampaignChip[];
	onCampaignToggle: (campaign: CampaignChip) => void;

	senderUserIds: number[];
	onSenderChange: (userIds: number[]) => void;

	/** Hides pills a tab does not scope by. */
	hide?: { campaign?: boolean; sender?: boolean; date?: boolean };
	/** Rendered at the right end of the bar (e.g. the compare toggle). */
	trailing?: ReactNode;
	/** Stacks the pills for a narrow comparison column. */
	isStacked?: boolean;
}

/**
 * The Analytics filter bar: List / Campaign / Sender / Date as compact pills.
 *
 * Every filter is multi-select, so the pills show a count rather than a value once more
 * than one thing is picked — `List: 3 selected` reads at a glance where a comma-joined
 * list of names would wrap the bar.
 */
export const AnalyticsFilterBar: FunctionComponent<AnalyticsFilterBarProps> = ({
	dateFromToValue,
	onDateChange,
	listOptions,
	onListContentFilterToggle,
	selectedLists,
	onListSelect,
	campaigns,
	onCampaignToggle,
	senderUserIds,
	onSenderChange,
	hide,
	trailing,
	isStacked = false,
}) => {
	const { t } = useTranslation();
	const { options: senderOptions } = useSenderOptions();
	const listData = listOptions.lists;

	const activeCampaigns = campaigns.filter((campaign) => campaign.active);

	const listValue =
		selectedLists.length === 0
			? t("analytics.filters.all")
			: selectedLists.length === 1
				? selectedLists[0].title
				: t("analytics.filters.countSelected", { count: selectedLists.length });

	const campaignValue =
		campaigns.length === 0 || activeCampaigns.length === campaigns.length
			? t("analytics.filters.all")
			: t("analytics.filters.countOf", { count: activeCampaigns.length, total: campaigns.length });

	const senderValue =
		senderUserIds.length === 0
			? t("analytics.filters.all")
			: t("analytics.filters.countSelected", { count: senderUserIds.length });

	return (
		<div
			className={classNames(
				"sticky z-30 flex flex-row flex-wrap items-center gap-2 bg-white border-b border-border-light py-2 mb-2",
				// Full-bleed like the header above it, so nothing scrolls past beside the pinned bar.
				// A comparison column keeps its own width: two bars share the row there.
				isStacked ? "" : "-mx-4 px-4 w-[calc(100%_+_2rem)]"
			)}
			data-testid="analytics-filter-bar"
			// Pins flush under the page header, which publishes its measured height (PREC-4029).
			// The 0.75rem cancels the outlet's pt-3, exactly like the header's own -top-3.
			style={{ top: `calc(var(${ANALYTICS_HEADER_HEIGHT_VAR}, 0px) - 0.75rem)` }}
		>
			<div
				className={classNames(
					"flex gap-2",
					isStacked ? "flex-col items-stretch w-full" : "flex-row flex-wrap items-center"
				)}
			>
				<FilterPill
					icon={faLayerGroup}
					label={t("analytics.filters.list")}
					testId="analytics-filter-list"
					value={listValue}
				>
					{() => (
						<FilterPillCheckList
							allLabel={t("analytics.filters.allLists")}
							emptyLabel={t("analytics.filters.noLists")}
							hasMore={listOptions.hasMore}
							header={
								<ListFilterQuickFilters
									contentFilter={listOptions.contentFilter}
									isApplying={listOptions.isFetchingAll}
									onToggle={onListContentFilterToggle}
								/>
							}
							isFetchingMore={listOptions.isFetching}
							loadMoreLabel={t("analytics.filters.loadMoreLists")}
							// Picked lists stay offered outside the current search and page (PREC-4434).
							options={selectedOptionsFirst(
								selectedLists.map((list) => ({ id: list.id as number, title: list.title })),
								listData.map((list) => ({ id: list.id, title: list.name }))
							)}
							searchPlaceholder={t("analytics.filters.searchLists")}
							selectedIds={selectedLists.map((list) => list.id as number)}
							onLoadMore={listOptions.onLoadMore}
							onSearch={listOptions.onSearch}
							onSelectAll={() => onListSelect([])}
							onToggle={(id) => {
								const isSelected = selectedLists.some((list) => list.id === id);

								if (isSelected) {
									onListSelect(selectedLists.filter((list) => list.id !== id));

									return;
								}

								const list = listData.find((candidate) => candidate.id === id);

								if (list) {
									onListSelect([...selectedLists, toSelectedList(list)]);
								}
							}}
						/>
					)}
				</FilterPill>

				{hide?.campaign ? null : (
					<FilterPill
						disabled={campaigns.length === 0}
						icon={faBullhorn}
						label={t("analytics.filters.campaign")}
						testId="analytics-filter-campaign"
						value={campaignValue}
					>
						{() => (
							<FilterPillCheckList
								allLabel={t("analytics.filters.allCampaigns")}
								emptyLabel={t("analytics.filters.noCampaigns")}
								options={campaigns.map((campaign) => ({
									id: campaign.campaignId,
									title: campaign.campaignName || campaign.campaignId,
									subtitle: campaign.listName,
								}))}
								selectedIds={activeCampaigns.map((campaign) => campaign.campaignId)}
								onSelectAll={() =>
									campaigns
										.filter((campaign) => !campaign.active)
										.forEach((campaign) => onCampaignToggle(campaign))
								}
								onToggle={(id) => {
									const campaign = campaigns.find((candidate) => candidate.campaignId === id);

									if (campaign) {
										onCampaignToggle(campaign);
									}
								}}
							/>
						)}
					</FilterPill>
				)}

				{hide?.sender ? null : (
					<FilterPill
						icon={faUsers}
						label={t("analytics.filters.sender")}
						testId="analytics-filter-sender"
						value={senderValue}
					>
						{() => (
							<FilterPillCheckList
								allLabel={t("analytics.filters.allSenders")}
								emptyLabel={t("analytics.filters.noSenders")}
								options={senderOptions}
								selectedIds={senderUserIds}
								onSelectAll={() => onSenderChange([])}
								onToggle={(id) =>
									onSenderChange(
										senderUserIds.includes(id as number)
											? senderUserIds.filter((userId) => userId !== id)
											: [...senderUserIds, id as number]
									)
								}
							/>
						)}
					</FilterPill>
				)}

				{hide?.date ? null : (
					<DateFilterPill isStacked={isStacked} value={dateFromToValue} onChange={onDateChange} />
				)}
			</div>

			{trailing ? <div className="ml-auto">{trailing}</div> : null}
		</div>
	);
};
